import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logging from "../utils/logging";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Camera, ScanLine, ShieldCheck, ShieldAlert, MapPin, Loader2, AlertTriangle, Eye, ArrowLeft, Share2, TrendingUp, Flame } from "lucide-react";
import { preprocessForPlateOCR, cropToPlateStrip, captureEvidenceThumbnail, upscaleForOCR, invertedDataURL } from "../utils/ocrPreprocess";
import { scanPlate } from "../utils/scanPlate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Re-enabled for testing the new Claude-only OCR pipeline.
const SHOW_PHOTO_CAPTURE = true;

/**
 * Public stolen-vehicle lookup page — no login required.
 * Photograph or type a plate, share GPS, and we'll tell you instantly whether it's stolen.
 * If it is, we silently fire the recovery loop (owner email + push + security alert).
 */
export default function LookupPage() {
  const { t } = useTranslation();
  const [plate, setPlate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [preview, setPreview] = useState(null);
  const [shareCount, setShareCount] = useState(0);
  const [trending, setTrending] = useState([]);
  const [recoveryStats, setRecoveryStats] = useState(null);
  // Track the most recent OCR prediction so we can post a feedback row
  // when the user finally submits — that's how we measure real accuracy
  // (predicted vs whatever the user actually kept after editing).
  const [ocrLast, setOcrLast] = useState({ image_hash: "", predicted: "" });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch the public leaderboard once on mount; refreshed implicitly when the user navigates away/back
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/epass/most-shared?days=7&limit=5`);
        setTrending(r.data || []);
      } catch { setTrending([]); }
      try {
        const s = await axios.get(`${API}/epass/recovery-stats?days=30`);
        setRecoveryStats(s.data);
      } catch { setRecoveryStats(null); }
    })();
  }, []);

  /**
   * Best-effort GPS acquisition. Resolves to `{latitude, longitude}` on
   * success, or `null` on any failure (no API, permission denied, timeout).
   * The backend `/epass/lookup` accepts a null pair and still returns the
   * stolen/good/unknown verdict — GPS is used to tag the sighting for
   * recovery teams, but it should never block the citizen from getting
   * their answer.
   */
  const getLocationBestEffort = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
    );
  });

  const doLookup = useCallback(async (plateNum, evidenceThumbnail = null, fbImageHash = "", fbPredicted = "") => {
    if (!plateNum.trim()) { toast.error(t("lookup.enter_plate")); return; }
    setBusy(true);
    setResult(null);
    try {
      // GPS is optional on the public /lookup page. If the user has blocked
      // location or the browser can't get a fix in 5 s, we proceed without
      // coordinates — still better than a dead-end error toast.
      const loc = await getLocationBestEffort();
      const res = await axios.post(`${API}/epass/lookup`, {
        plate_number: plateNum.trim(),
        latitude: loc?.latitude ?? null,
        longitude: loc?.longitude ?? null,
        evidence_thumbnail: evidenceThumbnail,
      });
      const enriched = { ...res.data, plate: plateNum.trim().toUpperCase() };
      setResult(enriched);
      // Fetch existing share count if the plate is stolen
      if (enriched.result === "stolen") {
        try {
          const sc = await axios.get(`${API}/epass/share-count/${encodeURIComponent(enriched.plate)}`);
          setShareCount(sc.data?.count || 0);
        } catch { setShareCount(0); }
      } else {
        setShareCount(0);
      }
      if (res.data.result === "stolen") toast.error(t("lookup.stolen_toast"));
      else if (res.data.result === "good") toast.success(t("lookup.good_toast"));
      else toast.info(t("lookup.unknown_toast"));

      // OCR accuracy telemetry — if a recent OCR prediction is associated
      // with this submit, record whether the user kept it as-is or corrected it.
      const imageHash = fbImageHash || ocrLast.image_hash;
      const predicted = fbPredicted || ocrLast.predicted;
      if (imageHash) {
        try {
          await axios.post(`${API}/ocr/feedback`, {
            image_hash: imageHash,
            predicted_plate: predicted,
            final_plate: enriched.plate,
            source: "lookup",
          });
        } catch { /* feedback is best-effort */ }
        setOcrLast({ image_hash: "", predicted: "" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("lookup.lookup_failed"));
    } finally { setBusy(false); }
  }, [ocrLast, t]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setShowCamera(false);
  }, []);

  const startCamera = async () => {
    setPreview(null);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 100);
    } catch (err) {
      logging.warn("Camera failed", err);
      toast.error(t("lookup.camera_unavailable"));
      setShowCamera(false);
    }
  };

  const capturePlate = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.videoWidth === 0) { toast.error(t("lookup.camera_not_ready")); return; }
    setBusy(true);
    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      // Save raw frame for the user-facing preview, then crop to the yellow
      // guide region (90% × 30%, centred) and preprocess in-place
      // (grayscale + contrast stretch + Otsu threshold) before feeding Tesseract.
      const rawDataUrl = canvas.toDataURL("image/jpeg", 0.9);
      setPreview(rawDataUrl);
      cropToPlateStrip(canvas);
      // Capture the COLOUR cropped strip as Plate Pin evidence (before we
      // mutate the canvas to monochrome). Sent with the lookup so admins can
      // see the actual evidence the citizen photographed.
      const evidenceThumbnail = captureEvidenceThumbnail(canvas);
      // Up-scale small captures so Tesseract sees ~320 px tall text.
      upscaleForOCR(canvas);
      const processedDataUrl = preprocessForPlateOCR(canvas);
      const inverted = invertedDataURL(canvas);
      stopCamera();
      const { plate: detected, engine, confidence, image_hash } = await scanPlate(processedDataUrl, inverted);
      if (!detected) { toast.error(t("lookup.no_plate")); setBusy(false); return; }
      setPlate(detected);
      setOcrLast({ image_hash: image_hash || "", predicted: detected });
      const engineLabel = engine === "tesseract" ? t("lookup.engine_fast") : t("lookup.engine_ai");
      toast.success(t("lookup.detected", { plate: detected, engine: engineLabel, conf: Math.round(confidence) }));
      await doLookup(detected, evidenceThumbnail, image_hash || "", detected);
    } catch (err) {
      logging.warn("OCR failed", err);
      toast.error(t("lookup.ocr_failed"));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex flex-col items-center px-4 py-6" data-testid="lookup-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 font-body mb-4 transition" data-testid="back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("lookup.back")}
        </Link>

        <div className="text-center mb-5">
          <h1 className="font-heading text-3xl font-black tracking-tighter text-gray-900">{t("lookup.title")}</h1>
          <p className="text-sm text-gray-500 font-body mt-1">{SHOW_PHOTO_CAPTURE ? t("lookup.subtitle_camera") : t("lookup.subtitle_typing")}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-5 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("lookup.plate_label")}</Label>
            <div className="flex gap-2">
              <Input
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="ABC-123-XY"
                className="rounded-sm font-mono flex-1"
                data-testid="lookup-plate-input"
              />
              <Button onClick={() => doLookup(plate)} disabled={busy} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body" data-testid="lookup-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-1" /> {t("lookup.check")}</>}
              </Button>
            </div>
          </div>

          <div className="text-center text-xs text-gray-400 font-body">{SHOW_PHOTO_CAPTURE ? t("common.or") : ""}</div>

          {SHOW_PHOTO_CAPTURE && (!showCamera ? (
            <Button variant="outline" onClick={startCamera} disabled={busy} className="w-full rounded-sm font-body text-sm" data-testid="lookup-camera-btn">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("lookup.reading")}</>) : (<><Camera className="w-4 h-4 mr-2" /> {t("lookup.photograph")}</>)}
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="relative rounded-sm overflow-hidden border border-gray-200 bg-black aspect-video">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                <div className="absolute left-[14%] right-[14%] top-1/2 -translate-y-1/2 h-20 border-2 border-amber-400 rounded-sm pointer-events-none" />
                <div className="absolute bottom-1.5 left-2 right-2 text-[10px] text-white/80 font-body text-center">
                  {t("lookup.frame_hint")}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={capturePlate} disabled={busy} className="flex-1 bg-black hover:bg-gray-800 text-white rounded-sm font-body" data-testid="lookup-capture-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" />{t("lookup.capture")}</>}
                </Button>
                <Button variant="outline" onClick={stopCamera} disabled={busy} className="rounded-sm font-body text-sm">{t("lookup.cancel")}</Button>
              </div>
            </div>
          ))}
          <canvas ref={canvasRef} className="hidden" />

          {SHOW_PHOTO_CAPTURE && preview && !showCamera && (
            <div className="border border-gray-200 rounded-sm p-2">
              <p className="text-[10px] text-gray-400 font-body mb-1 flex items-center gap-1">
                <Eye className="w-3 h-3" /> {t("lookup.last_frame")}
              </p>
              <img src={preview} alt="Captured plate" className="w-full rounded-sm" />
            </div>
          )}
        </div>

        {result && (
          <div
            className={`mt-4 border rounded-sm p-5 ${
              result.result === "stolen"
                ? "bg-red-50 border-red-300"
                : result.result === "good"
                ? "bg-green-50 border-green-300"
                : "bg-yellow-50 border-yellow-300"
            }`}
            data-testid="lookup-result"
          >
            <div className="flex items-center gap-2 mb-2">
              {result.result === "stolen" && <ShieldAlert className="w-6 h-6 text-red-600" />}
              {result.result === "good" && <ShieldCheck className="w-6 h-6 text-green-600" />}
              {result.result === "unknown" && <AlertTriangle className="w-6 h-6 text-yellow-600" />}
              <span className="font-heading text-xl font-bold">
                {result.result === "stolen" ? t("lookup.stolen") : result.result === "good" ? t("lookup.good") : t("lookup.unknown")}
              </span>
            </div>
            <p className="font-mono text-base font-bold text-gray-800 mb-2">{result.plate}</p>
            {result.result === "stolen" && (
              <div className="bg-white rounded-sm p-3 mb-2 border border-red-200">
                <p className="text-[10px] uppercase tracking-widest font-bold text-red-700 mb-1">{t("lookup.vehicle_desc")}</p>
                <p className="text-sm font-body text-gray-800">
                  {[result.color, result.make, result.model].filter(Boolean).join(" ") || t("lookup.desc_unavailable")}
                </p>
              </div>
            )}
            <p className="text-sm font-body text-gray-700 leading-relaxed">{result.message}</p>
            {result.result === "stolen" && (
              <p className="text-xs text-gray-500 font-body mt-3 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {t("lookup.gps_shared")}
              </p>
            )}
            {result.result === "stolen" && (
              <>
                <button
                  onClick={async () => {
                    const text = `🚨 STOLEN VEHICLE — keep an eye out!\n\nPlate: ${result.plate}\nVehicle: ${[result.color, result.make, result.model].filter(Boolean).join(" ") || "details unavailable"}\n\nSpot it? Confirm anonymously with Guard: ${window.location.origin}/lookup`;
                    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
                    // Open WhatsApp first (avoids popup-blocker issues), then fire & forget the tracker
                    window.open(url, "_blank", "noopener,noreferrer");
                    try {
                      const r = await axios.post(`${API}/epass/share-track`, { plate_number: result.plate, channel: "whatsapp" });
                      setShareCount(r.data?.count || (shareCount + 1));
                    } catch {
                      // Optimistic bump even if tracker errors — UX should never feel broken
                      setShareCount((c) => c + 1);
                    }
                  }}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-body font-bold text-sm px-3 py-2 rounded-sm transition"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 className="w-4 h-4" /> {t("lookup.share_whatsapp")}
                </button>
                {shareCount > 0 && (
                  <p className="text-[11px] text-center text-gray-500 font-body mt-1.5" data-testid="share-counter">
                    <span className="font-bold text-gray-700">{t(shareCount === 1 ? "lookup.shared_by_one" : "lookup.shared_by", { n: shareCount.toLocaleString() })}</span>{t("lookup.shared_help")}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <p className="text-[10px] text-gray-400 font-body text-center mt-6 leading-relaxed">
          {t("lookup.disclaimer")}
        </p>

        {/* Most-shared leaderboard */}
        {trending.length > 0 && (
          <div className="mt-6 bg-white border border-gray-200 rounded-sm" data-testid="trending-panel">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200">
              <Flame className="w-4 h-4 text-rose-600" />
              <h2 className="font-heading text-sm font-bold text-gray-900">{t("lookup.trending_title")}</h2>
              <span className="text-[10px] text-gray-400 font-body ml-auto flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> {t("lookup.trending_hunted")}
              </span>
            </div>
            {recoveryStats?.recovered_count > 0 && (
              <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-xs text-green-800 font-body" data-testid="recovery-stat">
                {t(recoveryStats.recovered_count === 1 ? "lookup.recovered_count_one" : "lookup.recovered_count", { n: recoveryStats.recovered_count.toLocaleString(), days: recoveryStats.days_window })}
              </div>
            )}
            <ol className="divide-y divide-gray-100">
              {trending.map((row, i) => {
                const veh = [row.color, row.make, row.model].filter(Boolean).join(" ");
                const text = `🚨 STOLEN VEHICLE — keep an eye out!\n\nPlate: ${row.plate_number}\nVehicle: ${veh || "details unavailable"}\n\nSpot it? Confirm anonymously with Guard: ${window.location.origin}/lookup`;
                const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                return (
                  <li key={row.plate_number} className={`flex items-center gap-3 px-4 py-2.5 ${row.recovered ? "bg-green-50/40" : ""}`} data-testid={`trending-row-${i + 1}`}>
                    <span className={`text-base font-heading font-black w-5 text-center ${row.recovered ? "text-green-700" : "text-rose-600"}`}>{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-mono text-sm font-bold text-gray-900 truncate">{row.plate_number}</p>
                        {row.recovered && (
                          <span className="text-[9px] uppercase tracking-widest font-bold text-green-700 bg-green-100 rounded-sm px-1.5 py-0.5 inline-flex items-center gap-0.5" data-testid={`recovered-badge-${i + 1}`}>
                            <ShieldCheck className="w-2.5 h-2.5" /> {t("lookup.recovered_badge")}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 font-body truncate">{veh || t("lookup.vehicle_unavailable")}</p>
                    </div>
                    <span className="text-[11px] font-body font-bold text-gray-700 bg-gray-100 rounded-sm px-1.5 py-0.5 whitespace-nowrap">
                      {t(row.shares === 1 ? "lookup.share_count_one" : "lookup.share_count", { n: row.shares.toLocaleString() })}
                    </span>
                    {!row.recovered && (
                      <button
                        onClick={async () => {
                          window.open(waUrl, "_blank", "noopener,noreferrer");
                          try {
                            await axios.post(`${API}/epass/share-track`, { plate_number: row.plate_number, channel: "whatsapp" });
                            setTrending((cur) => cur.map((r2) => r2.plate_number === row.plate_number ? { ...r2, shares: r2.shares + 1 } : r2));
                          } catch { /* share-track is fire-and-forget for analytics; UX must never feel broken if it fails */ }
                        }}
                        className="bg-[#25D366] hover:bg-[#1ebe5b] text-white rounded-sm px-2 py-1 inline-flex items-center gap-1 transition"
                        title={t("lookup.share_to_whatsapp")}
                        data-testid={`trending-share-${i + 1}`}
                      >
                        <Share2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>
            <p className="text-[10px] text-gray-400 font-body px-4 py-2 border-t border-gray-100">
              {t("lookup.trending_footer")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
